import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'firebase/auth';

import { logEvent } from '~/config/firebase.config';
import { AuthError, useAuth } from '~/services/auth';

import { errorMessage } from './utils';
import { AuthShell, AuthField, AuthSubmit, AuthBack } from './AuthKit';
import styles from './auth.redesign.module.css';

/**
 * Forgot password page.
 */
export function ForgotPassword() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [alertMessage, setAlertMessage] = useState<string>('');
    const [alertType, setAlertType] = useState<'success' | 'error' | ''>('');

    useEffect(() => {
        logEvent('signin_view', 'SignIn');
        document.title = 'Sign In - Unboared';
    }, []);

    const { loading, resetError, emailErrorCode, firebaseAuth, sendResetLink } = useAuth();

    const handleForgotPassword = async (e: any) => {
        e.preventDefault();
        logEvent('forgot_password_click', 'SignIn');
        const formData = new FormData(e.target);
        const email = formData.get('email') as string;
        if (!email) {
            setAlertType('error');
            setAlertMessage(t(`global.error.${errorMessage(AuthError.MISSING_EMAIL)}`));
            return;
        }

        await sendResetLink(firebaseAuth as Auth, email);

        setAlertType('success');
        setAlertMessage(t('global.login.emailLinkSent'));
    };

    return (
        <AuthShell>
            <form onSubmit={handleForgotPassword}>
                <AuthBack onClick={() => navigate('/signin')} label={t('global.login.title')} />

                <h2 className={styles.title}>{t('global.login.resetPassword')}</h2>
                <p className={styles.subtitle}>{t('global.login.resetPasswordText')}</p>

                <AuthField
                    id="email"
                    label={t('global.login.input.email')}
                    placeholder={t('global.login.input.emailPlaceholder')}
                    autoComplete="email"
                    onSelect={resetError}
                    error={emailErrorCode !== undefined}
                    helper={
                        emailErrorCode !== undefined ? t(`global.error.${errorMessage(emailErrorCode)}`) : undefined
                    }
                />

                {alertMessage && (
                    <p
                        className={`${styles.alert} ${
                            alertType === 'success' ? styles.alertSuccess : styles.alertError
                        }`}
                    >
                        {alertMessage}
                    </p>
                )}

                <AuthSubmit loading={loading}>{t('global.login.buttonResetPassword')}</AuthSubmit>
            </form>
        </AuthShell>
    );
}
